import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useAuth, ROLES, MODULES, ROLE_LABELS } from "../../context/AuthContext";
import Loader from "../../components/Loader";

const AdminList = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  const { user } = useAuth();

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/list`,
        {
          params: { search, page, limit },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setAdmins(response?.data?.data || []);
      setPagination(response?.data?.pagination || {});
    } catch (error) {
      console.error("Error fetching admins:", error);
      if (error?.response?.status === 403) {
        toast.error("You don't have permission to view admins");
      } else {
        toast.error("Failed to fetch admins");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [page, search]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleDelete = async (adminId, adminName) => {
    if (adminId === user?._id) {
      toast.error("You cannot delete yourself");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete admin "${adminName}". This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/admin/delete?id=${adminId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          toast.success("Admin deleted successfully");
          fetchAdmins();
        } catch (error) {
          console.error("Error deleting admin:", error);
          const errorMessage =
            error?.response?.data?.message || "Failed to delete admin";
          toast.error(errorMessage);
        }
      }
    });
  };

  const allModulesCount = Object.values(MODULES).length;

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN:
        return "badge badge-danger";
      case ROLES.SUB_ADMIN:
        return "badge badge-primary";
      default:
        return "badge badge-light";
    }
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Admin Management</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Admin Management</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Admin List</h3>
                  <div className="card-tools">
                    <Link to="/admin-management/create" className="btn btn-primary btn-sm">
                      <i className="fas fa-plus"></i> Create Admin
                    </Link>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name or email..."
                        value={search}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>

                  {loading ? (
                    <Loader />
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-bordered table-striped">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Role</th>
                              <th>Permissions</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {admins.length > 0 ? (
                              admins.map((admin, index) => (
                                <tr key={admin._id}>
                                  <td>{(page - 1) * limit + index + 1}</td>
                                  <td>
                                    {admin.name}
                                    {admin._id === user?._id && (
                                      <span className="badge badge-info ml-2">You</span>
                                    )}
                                  </td>
                                  <td>{admin.email}</td>
                                  <td>
                                    <span className={getRoleBadgeClass(admin.role)}>
                                      {ROLE_LABELS[admin.role] || admin.role?.replace("_", " ").toUpperCase()}
                                    </span>
                                  </td>
                                  <td>
                                    {admin.role === ROLES.SUPER_ADMIN ? (
                                      <span className="text-success">
                                        <i className="fas fa-check-circle mr-1"></i>
                                        All Modules
                                      </span>
                                    ) : (
                                      <span className="text-muted">
                                        {admin.permissions?.length || 0} / {allModulesCount} modules
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <Link
                                      to={`/admin-management/update-role/${admin._id}`}
                                      className="btn btn-sm btn-warning mr-2"
                                      title="Edit Permissions"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </Link>
                                    {admin._id !== user?._id && (
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDelete(admin._id, admin.name)}
                                        title="Delete Admin"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center">
                                  No admins found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <div>
                            Showing {(page - 1) * limit + 1} to{" "}
                            {Math.min(page * limit, pagination.total)} of{" "}
                            {pagination.total} entries
                          </div>
                          <nav>
                            <ul className="pagination mb-0">
                              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                                <button
                                  className="page-link"
                                  onClick={() => setPage(page - 1)}
                                  disabled={page === 1}
                                >
                                  Previous
                                </button>
                              </li>
                              {[...Array(pagination.totalPages)].map((_, i) => (
                                <li
                                  key={i + 1}
                                  className={`page-item ${page === i + 1 ? "active" : ""}`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setPage(i + 1)}
                                  >
                                    {i + 1}
                                  </button>
                                </li>
                              ))}
                              <li
                                className={`page-item ${page === pagination.totalPages ? "disabled" : ""
                                  }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setPage(page + 1)}
                                  disabled={page === pagination.totalPages}
                                >
                                  Next
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminList;
